import { getOnboardingRoute, isOnboardingComplete } from "./onboardingNavigation";
import { getUserProfile } from "../Services/MyProfile";
import Cookies from "js-cookie";

/**
 * Handles post-login flow including onboarding routing and coaching mode setup
 * @param {Object} options - Configuration options
 * @param {Function} options.navigate - React Router navigate function
 * @param {Function} options.addProfileData - Header context addProfileData function
 * @param {string} options.redirectUri - Optional redirect URI from URL params
 * @param {Function} options.initializeFCM - Optional FCM initialization function
 * @returns {Promise<void>}
 */
export const handlePostLoginFlow = async ({
  navigate,
  addProfileData,
  redirectUri = null,
  initializeFCM = null,
}) => {
  try {
    // Fetch fresh user profile
    const res = await getUserProfile();
    
    // Persist profile for downstream guards
    localStorage.setItem("profile", JSON.stringify(res));
    addProfileData(res);

    // Set flag to show welcome back screen when user reaches onboarding
    try {
      localStorage.setItem('showWelcomeScreen', 'true');
    } catch (_) {}

    // Set user cookies
    const { first_name, last_name } = res;
    if (first_name) Cookies.set("firstName", first_name);
    if (last_name) Cookies.set("lastName", last_name);

    // Initialize FCM if provided
    if (initializeFCM) {
      initializeFCM();
    }

    const { onboarding_step } = res;

    // Centralized onboarding routing (skips subscription when setup exists)
    if (onboarding_step && !isOnboardingComplete(onboarding_step)) {
      const route = getOnboardingRoute(onboarding_step, res);
      navigate(route);
      return;
    }

    // If onboarding complete or not present, sync coaching mode and walkthrough flags
    syncCoachingModeAndWalkthrough(res);

    // Navigate to appropriate destination
    navigateToHome({ navigate, redirectUri });

  } catch (error) {
    console.error("Post-login flow error:", error);
    throw new Error(
      error?.response?.data?.message ||
      error?.message ||
      "Something went wrong. Please try again later"
    );
  }
};

/**
 * Syncs coaching mode and walkthrough flags from profile to localStorage
 * @param {Object} profile - User profile object
 */
export const syncCoachingModeAndWalkthrough = (profile) => {
  try {
    const settings = profile?.meta_info?.settings;
    
    if (settings?.coaching_mode === 'keyboard') {
      localStorage.setItem("coaching_mode", 'keyboard');
      localStorage.setItem("keyboard_walkthrough_done", settings?.keyboard_walkthrough_done || false);
    } else {
      localStorage.setItem("coaching_mode", 'voice');
      localStorage.setItem("voice_walkthrough_done", settings?.voice_walkthrough_done || false);
    }
  } catch (error) {
    console.error("Error syncing coaching mode and walkthrough:", error);
  }
};

/**
 * Navigates user to appropriate home page based on coaching mode or redirect URI
 * @param {Object} options - Navigation options
 * @param {Function} options.navigate - React Router navigate function
 * @param {string} options.redirectUri - Optional redirect URI
 */
export const navigateToHome = ({ navigate, redirectUri }) => {
  const isSafePath = (p) => typeof p === 'string' && p.startsWith('/') && !p.startsWith('//');
  
  if (isSafePath(redirectUri)) {
    navigate(redirectUri, { replace: true });
  } else {
    // Navigate to dashboard (coaching mode is handled by dashboard component)
    navigate("/dashboard", { replace: true });
  }
};
